/**
 * Duration and timestamp formatting shared across the triage view.
 */

import { dateLocaleStringWithTimeZone } from '../../../../../Common/util/DateUtils';

export const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
        return '-';
    }
    if (seconds < 60) {
        return `${Math.round(seconds)}s`;
    }
    if (seconds < 3600) {
        return `${Math.round(seconds / 60)}m`;
    }
    if (seconds < 86400) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.round((seconds % 3600) / 60);
        return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
    }
    const days = Math.floor(seconds / 86400);
    const hours = Math.round((seconds % 86400) / 3600);
    return hours === 0 ? `${days}d` : `${days}d ${hours}h`;
};

export const formatAge = (isoDate: string | null | undefined): string => {
    if (!isoDate) {
        return '-';
    }
    const elapsedMs = Date.now() - new Date(isoDate).getTime();
    if (Number.isNaN(elapsedMs)) {
        return '-';
    }
    return formatDuration(elapsedMs / 1000);
};

export const ageInHours = (isoDate: string | null | undefined): number => {
    if (!isoDate) {
        return 0;
    }
    return (Date.now() - new Date(isoDate).getTime()) / 3600_000;
};

export const formatDateTime = (isoDate: string | null | undefined): string => {
    if (!isoDate) {
        return '-';
    }
    const date = new Date(isoDate);
    return Number.isNaN(date.getTime())
        ? '-'
        : dateLocaleStringWithTimeZone(date);
};

export const formatUtcDateTime = (
    isoDate: string | null | undefined
): string => {
    if (!isoDate) return '';
    const zoned = /(?:Z|[+-]\d{2}:\d{2})$/i.test(isoDate)
        ? isoDate
        : `${isoDate}Z`;
    const date = new Date(zoned);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

export const formatRelativeToNow = (date: Date | null): string => {
    if (!date) {
        return 'never';
    }
    const seconds = (Date.now() - date.getTime()) / 1000;
    if (seconds < 45) {
        return 'just now';
    }
    return `${formatDuration(seconds)} ago`;
};
